/**
 * PageHeader — canonical page title strip.
 *
 * Full-width surface with a 1px border-bottom that anchors every
 * top-level page in the redesigned shell (settings, edit forms,
 * order edit/create, etc.). Title + optional subtitle on the left,
 * optional `meta` row below the subtitle (status pills, last-updated
 * timestamps), and an `actions` slot on the right (Save buttons,
 * filters, etc.).
 *
 * Type scale matches the production design system: 26 px Lato title
 * with `-0.4px` letter-spacing, 13.5 px subtitle capped at 560 px so
 * long descriptions wrap without dragging the header height.
 *
 *   <PageHeader
 *     title="Edit Profile"
 *     subtitle="Update your personal and contact information."
 *     actions={<Button>Save</Button>}
 *   />
 *
 *   <PageHeader
 *     title="Edit Order"
 *     subtitle="Review and update treatment details."
 *     meta={<StatusBadge>Pending</StatusBadge>}
 *   />
 */
import { styled } from '@mui/material/styles';
import { SkeletonBar } from '../Skeleton';

const Root = styled('header')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  padding: '22px 28px 20px',
  borderBottom: '1px solid var(--qhs-border)',
  background: 'var(--qhs-surface)',
  flexShrink: 0,
});

const Titles = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Title = styled('h1')({
  margin: 0,
  fontFamily: "'Lato', sans-serif",
  fontSize: 26,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  letterSpacing: '-0.4px',
  lineHeight: 1.2,
});

const Subtitle = styled('p')({
  margin: '4px 0 0',
  fontSize: 13.5,
  color: 'var(--qhs-text-2)',
  lineHeight: 1.5,
  maxWidth: 560,
});

const Meta = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginTop: 10,
  fontSize: 12,
  color: 'var(--qhs-text-2)',
});

const Actions = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
});

const PageHeader = ({ title, subtitle, meta, actions, className, loading }) => (
  <Root className={className}>
    <Titles>
      {loading ? (
        // Placeholder while an async title (e.g. a fetched account name)
        // resolves; sized to the Title's line so the header does not jump.
        <SkeletonBar variant="text" width={260} height={34} />
      ) : (
        title && <Title>{title}</Title>
      )}
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
      {meta && <Meta>{meta}</Meta>}
    </Titles>
    {actions && <Actions>{actions}</Actions>}
  </Root>
);

export default PageHeader;
